import { useEffect, useRef, useMemo, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'

import {
  getOrgEventsForReport,
  getWeatherEventAnalytics,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { EventSelector } from '../EventSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import WeatherEventAnalyticsStaffing from './WeatherEventAnalyticsStaffing'
import WeatherEventAnalyticsSummary from './WeatherEventAnalyticsSummary'
import { confidenceLabel } from './weatherEventAnalyticsUtils'

import './weatherEventAnalyticsWrapper.scss'

export default function WeatherEventAnalyticsWrapper({ organization }) {
  const [events, setEvents] = useState([])
  const [eventUUID, setEventUUID] = useState('')
  const [reportData, setReportData] = useState(null)
  const [isLoadingEvents, setIsLoadingEvents] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingReport)
  const [loadError, setLoadError] = useState(null)
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const organizationUUID = organization?.uuid

  const TUTORIAL_VIDEO_SOURCE =
    'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Weather%20Event%20Analytics_1080p.mp4'

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingEvents(true)

    getOrgEventsForReport(organizationUUID)
      .then((res) => {
        if (cancelled) return
        setEvents(Array.isArray(res?.data) ? res.data : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load events', err)
        setLoadError('Could not load events for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEvents(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID || !eventUUID) {
      setReportData(null)
      return
    }

    let cancelled = false
    setIsLoadingReport(true)
    setLoadError(null)

    getWeatherEventAnalytics(organizationUUID, eventUUID)
      .then((res) => {
        if (cancelled) return
        setReportData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load weather event analytics', err)
        setLoadError('Could not load weather analytics for this event.')
        setReportData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingReport(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID, eventUUID, refreshToken])

  const selectedEvent = useMemo(
    () => events.find((event) => event.uuid === eventUUID) || null,
    [events, eventUUID]
  )

  const isLoading = isLoadingEvents || isLoadingReport
  const analytics = reportData?.analytics
  const hasData = analytics && !reportData?.error

  return (
    <section
      className="spacer-sm weather-event-analytics-wrapper"
      id="weather-event-analytics-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Weather Event Analytics</h1>
        </div>
        <p className="section-header-desc">
          Measure how weather affected attendance, walk-up sales, scan rate, and
          staffing for {organization?.name || 'your organization'}.
        </p>
        <p className="section-header-desc mt-2">
          <strong>Note: </strong>
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => setShowTutorial(true)}
          >
            View Tutorial
          </Button>
        </p>
      </header>

      <Modal
        show={showTutorial}
        onHide={handleCloseTutorial}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Weather Event Analytics Tutorial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <video
            ref={tutorialVideoRef}
            controls
            autoPlay
            style={{ width: '100%' }}
          >
            <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Modal.Body>
      </Modal>

      <Card
        body
        className="card--xs card-with-border weather-event-analytics-controls"
      >
        <Stack
          direction="horizontal"
          gap={3}
          className="align-items-end flex-wrap"
        >
          <EventSelector
            events={events}
            selected={eventUUID}
            setSelected={setEventUUID}
            isLoading={isLoadingEvents}
            groupByYear
          />
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingReport}
            disabled={!eventUUID || isLoadingReport}
          />
        </Stack>
        {selectedEvent && (
          <p className="text-muted small mb-0 mt-2">
            Showing weather analytics for <strong>{selectedEvent.name}</strong>
            {analytics?.comparableCount != null && (
              <>
                {' '}
                · benchmarked against {analytics.comparableCount} similar events
              </>
            )}
          </p>
        )}
      </Card>

      {loadError && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      )}

      {isLoading && eventUUID && <PageLoadingContainer />}

      {!isLoading && eventUUID && reportData?.error === 'event_not_found' && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Event not found for this organization.
          </p>
        </Card>
      )}

      {!isLoading &&
        eventUUID &&
        reportData?.error &&
        reportData?.error !== 'event_not_found' && (
          <Card body className="card--xs card-with-border mt-3">
            <p className="text-muted mb-0">
              {reportData.message ||
                'Weather analytics are not available for this event yet.'}
            </p>
          </Card>
        )}

      {!isLoading && hasData && (
        <>
          <div className="mt-3">
            <WeatherEventAnalyticsSummary analytics={analytics} />
          </div>

          {analytics.weather && (
            <Card body className="card--xs card-with-border mt-3">
              <h2 className="h5 mb-2">Event weather</h2>
              <p className="mb-1">
                {analytics.weather.conditionText || 'Conditions recorded'}
                {analytics.weather.temperature != null && (
                  <> · {Math.round(analytics.weather.temperature)}°F</>
                )}
                {analytics.weather.precipitationProbability != null && (
                  <>
                    {' '}
                    · {Math.round(analytics.weather.precipitationProbability)}%
                    rain chance
                  </>
                )}
                {analytics.weather.windSpeed != null && (
                  <> · {Math.round(analytics.weather.windSpeed)} mph wind</>
                )}
              </p>
              <p className="text-muted small mb-0">
                Confidence: {confidenceLabel(analytics.confidenceScore)} (
                {Math.round(analytics.confidenceScore)}%)
                {reportData.source === 'live' ? ' · live preview' : ''}
              </p>
            </Card>
          )}

          {analytics.recommendedAction && (
            <Card
              body
              className="card--xs card-with-border mt-3 weather-recommendation-card"
            >
              <h2 className="h5 mb-2">Recommended action</h2>
              <p className="mb-0">{analytics.recommendedAction}</p>
            </Card>
          )}

          <WeatherEventAnalyticsStaffing analytics={analytics} />
        </>
      )}

      {!eventUUID && !isLoadingEvents && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Select an event to view weather event analytics.
          </p>
        </Card>
      )}
    </section>
  )
}
